import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  FormControlLabel,
  Checkbox,
  Slider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  IconButton,
  Divider,
  Alert,
} from '@mui/material';
import {
  Close,
  Traffic,
  Lightbulb,
  Check,
} from '@mui/icons-material';
import { getDevicesByLayout } from '../../api/deviceApi';
import { updateLineDevices } from '../../api/lineApi';

const LineForm = ({ 
  onSubmit, 
  line = null, 
  layoutId, 
  onClose, 
  open = false 
}) => {
  const [formData, setFormData] = useState({
    name: '',
    greenDuration: 30,
    yellowDuration: 5,
    direction: [],
    description: '',
    isActive: true,
    status: {
      signal: null,
      light: null,
      brightness: 50
    }
  });
  
  const [availableDevices, setAvailableDevices] = useState([]);
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const initialDeviceIds = useRef([]);

  useEffect(() => {
    if (line) {
      setFormData({
        name: line.name || '',
        greenDuration: line.greenDuration || 30,
        yellowDuration: line.yellowDuration || 5,
        direction: line.direction || [],
        description: line.description || '',
        isActive: line.isActive !== undefined ? line.isActive : true,
        status: {
          signal: line.status?.signal || null,
          light: line.status?.light || null,
          brightness: line.status?.brightness || 50
        }
      });
      let deviceIds = [];
      if (line.deviceIds) {
        if (Array.isArray(line.deviceIds)) {
          deviceIds = line.deviceIds;
        }
      }
      setSelectedDevices(deviceIds);
      initialDeviceIds.current = deviceIds;
    } else {
      setFormData({
        name: '',
        greenDuration: 30,
        yellowDuration: 5,
        direction: [],
        description: '',
        isActive: true,
        status: {
          signal: null,
          light: null,
          brightness: 50
        }
      });
      setSelectedDevices([]);
      initialDeviceIds.current = [];
    }
  }, [line]);

  useEffect(() => {
    if (layoutId) {
      fetchAvailableDevices();
    }
  }, [layoutId, line, open]);

  const fetchAvailableDevices = async () => {
    try {
      const data = await getDevicesByLayout(layoutId);
      setAvailableDevices(
        data.devices.filter(d => {
          if (!line || !line._id) {
            return !d.lineId;
          }
          return !d.lineId || d.lineId === line._id;
        }) || []
      );
    } catch (error) {
      console.error('Error fetching devices:', error);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleStatusChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      status: {
        ...prev.status,
        [field]: value
      }
    }));
  };

  const handleDirectionChange = (direction) => {
    setFormData(prev => ({
      ...prev,
      direction: prev.direction.includes(direction)
        ? prev.direction.filter(d => d !== direction)
        : [...prev.direction, direction]
    }));
  };

  const handleDeviceToggle = (deviceId) => {
    setSelectedDevices(prev => {
      const newSelection = prev.includes(deviceId)
        ? prev.filter(id => id !== deviceId)
        : [...prev, deviceId];
      return newSelection;
    });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Line name is required';
    }
    if (formData.greenDuration <= 0) {
      newErrors.greenDuration = 'Green duration must be greater than 0';
    }
    if (formData.yellowDuration <= 0) {
      newErrors.yellowDuration = 'Yellow duration must be greater than 0';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    try {
      if (line && line._id) {
        // Update mode: do not send deviceIds in updateLine
        const submitData = { ...formData };
        await onSubmit(submitData);
        // Only call updateLineDevices if deviceIds changed
        const prev = initialDeviceIds.current || [];
        const curr = selectedDevices || [];
        const changed = prev.length !== curr.length || prev.some(id => !curr.includes(id));
        if (changed) {
          await updateLineDevices(line._id, curr);
          // Refresh data after device update
          await onSubmit({ ...submitData, _refresh: true });
        } else {
          handleClose();
        }
      } else {
        // Create mode: send deviceIds as usual
        const submitData = { ...formData, layoutId, deviceIds: selectedDevices };
        await onSubmit(submitData);
        handleClose();
      }
    } catch (error) {
      console.error('Error submitting line:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      greenDuration: 30,
      yellowDuration: 5,
      direction: [],
      description: '',
      isActive: true,
      status: {
        signal: null,
        light: null,
        brightness: 50
      }
    });
    setSelectedDevices([]);
    fetchAvailableDevices();
    setErrors({});
    onClose();
  };

  const getDeviceIcon = (type) => {
    return type === 'signal' ? <Traffic /> : <Lightbulb />;
  };

  const getDeviceTypeLabel = (type) => {
    return type === 'signal' ? 'Traffic Signal' : 'Light';
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">
            {line ? 'Edit Line' : 'Create New Line'}
          </Typography>
          <IconButton onClick={handleClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
          <Grid container spacing={3}>
            {/* Basic Information */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                    Basic Information
                  </Typography>
                  <TextField
                    label="Line Name"
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    fullWidth
                    required
                    error={!!errors.name}
                    helperText={errors.name}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    label="Description"
                    value={formData.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                    fullWidth
                    multiline
                    rows={3}
                    sx={{ mb: 2 }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.isActive}
                        onChange={(e) => handleChange('isActive', e.target.checked)}
                      />
                    }
                    label="Line is active"
                  />
                </CardContent>
              </Card>
            </Grid>
            {/* Timing Configuration */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                    Timing Configuration
                  </Typography>
                  <TextField
                    label="Green Duration (seconds)"
                    type="number"
                    value={formData.greenDuration}
                    onChange={(e) => handleChange('greenDuration', parseInt(e.target.value))}
                    fullWidth
                    required
                    error={!!errors.greenDuration}
                    helperText={errors.greenDuration}
                    inputProps={{ min: 1 }}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    label="Yellow Duration (seconds)"
                    type="number"
                    value={formData.yellowDuration}
                    onChange={(e) => handleChange('yellowDuration', parseInt(e.target.value))}
                    fullWidth
                    required
                    error={!!errors.yellowDuration}
                    helperText={errors.yellowDuration}
                    inputProps={{ min: 1 }}
                    sx={{ mb: 2 }}
                  />
                </CardContent>
              </Card>
            </Grid>
            {/* Direction Configuration */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                    Traffic Direction
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {['straight', 'left', 'right'].map((dir) => (
                      <Chip
                        key={dir}
                        label={
                          dir === 'straight' ? 'Straight' :
                          dir === 'left' ? 'Left' : 'Right'
                        }
                        onClick={() => handleDirectionChange(dir)}
                        color={formData.direction.includes(dir) ? 'primary' : 'default'}
                        variant={formData.direction.includes(dir) ? 'filled' : 'outlined'}
                        clickable
                      />
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            {/* Status Configuration */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                    Default Status
                  </Typography>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Signal</InputLabel>
                    <Select
                      value={formData.status.signal || ''}
                      onChange={(e) => handleStatusChange('signal', e.target.value || null)}
                      label="Signal"
                    >
                      <MenuItem value="">Not set</MenuItem>
                      <MenuItem value="red">Red</MenuItem>
                      <MenuItem value="yellow">Yellow</MenuItem>
                      <MenuItem value="green">Green</MenuItem>
                      <MenuItem value="blinking">Blinking</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Light</InputLabel>
                    <Select
                      value={formData.status.light || ''}
                      onChange={(e) => handleStatusChange('light', e.target.value || null)}
                      label="Light"
                    >
                      <MenuItem value="">Not set</MenuItem>
                      <MenuItem value="on">On</MenuItem>
                      <MenuItem value="off">Off</MenuItem>
                    </Select>
                  </FormControl>
                  {formData.status.light === 'on' && (
                    <Box>
                      <Typography variant="body2" gutterBottom>
                        Brightness: {formData.status.brightness}%
                      </Typography>
                      <Slider
                        value={formData.status.brightness}
                        onChange={(e, value) => handleStatusChange('brightness', value)}
                        min={0}
                        max={100}
                        valueLabelDisplay="auto"
                      />
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
            {/* Device Selection */}
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                    Select Devices ({selectedDevices.length} selected)
                  </Typography>
                  {availableDevices.length === 0 ? (
                    <Alert severity="info">
                      No devices available in this layout
                    </Alert>
                  ) : (
                    <List sx={{ maxHeight: 300, overflow: 'auto' }}>
                      {availableDevices.map((device, index) => (
                        <React.Fragment key={device._id}>
                          <ListItem
                            button
                            onClick={() => handleDeviceToggle(device._id)}
                            selected={selectedDevices.includes(device._id)}
                            sx={{
                              '&.Mui-selected': {
                                backgroundColor: 'primary.light',
                                '&:hover': {
                                  backgroundColor: 'primary.main',
                                }
                              }
                            }}
                          >
                            <ListItemIcon>
                              {getDeviceIcon(device.type)}
                            </ListItemIcon>
                            <ListItemText
                              primary={device.name}
                              secondary={`${getDeviceTypeLabel(device.type)} - (${device.position.x}, ${device.position.y})`}
                            />
                            <ListItemSecondaryAction>
                              {selectedDevices.includes(device._id) && (
                                <Check color="primary" />
                              )}
                            </ListItemSecondaryAction>
                          </ListItem>
                          {index < availableDevices.length - 1 && <Divider />}
                        </React.Fragment>
                      ))}
                    </List>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        <Button onClick={handleClose} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          sx={{ minWidth: 120 }}
        >
          {loading ? 'Processing...' : (line ? 'Update' : 'Create Line')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LineForm; 